import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  formatIndianNumber,
  formatIndianNumberWithDecimals,
  calculateMaturityAmount,
  formatTimePeriod
} from '../../../utils/FD/fd'
import { convertNumberToCurrency } from '../../../utils/utils'

type FDInputPropTypes = {
  annualInterestRate: number
  timePeriodInYears: number
  maxAmount: number
  minAmount: number
  handleDisableButton: (isDisable: boolean) => void
  currentInvestment: number
  handleInputRelatedAmounts: ({
    currInvestment,
    matureAmount
  }: {
    currInvestment: number
    matureAmount: number
  }) => void
}

export const FDInput = ({
  annualInterestRate,
  timePeriodInYears,
  maxAmount,
  minAmount,
  handleDisableButton,
  currentInvestment,
  handleInputRelatedAmounts
}: FDInputPropTypes) => {
  const [inputValue, setInputValue] = useState(`${currentInvestment || 75000}`)
  const [isFocused, setIsFocused] = useState(false)
  const [isErrorCase, setIsErrorCase] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^\d.]/g, '')
    setInputValue(rawValue)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  const displayedValue = formatIndianNumber(inputValue)

  const calculatedMaturity = inputValue
    ? calculateMaturityAmount(parseFloat(inputValue), annualInterestRate, timePeriodInYears)
    : 0

  useEffect(() => {
    if (inputValue) {
      const amountEntered = parseFloat(inputValue)
      const isProb = amountEntered < minAmount || amountEntered > maxAmount
      setIsErrorCase(() => isProb)
      handleDisableButton(isProb)
    }
  }, [inputValue])

  useEffect(() => {
    handleInputRelatedAmounts({ currInvestment: parseFloat(inputValue), matureAmount: calculatedMaturity })
  }, [calculatedMaturity, inputValue])

  const maturesText = useMemo(() => {
    const amountEntered = parseFloat(inputValue)
    if (amountEntered < minAmount) return `Min. investment amount is ₹${formatIndianNumberWithDecimals(minAmount, 0)}`
    else if (amountEntered > maxAmount)
      return `This bank allows up to ${convertNumberToCurrency({ amount: maxAmount, isRound: false, isSmall: true })} per FD`
    else
      return `Matures to ~ ₹${inputValue ? formatIndianNumberWithDecimals(calculatedMaturity, 0) : '0'} in${' '}
        ${formatTimePeriod(timePeriodInYears)}`
  }, [inputValue])

  return (
    <div
      style={{ background: 'rgba(255, 255, 255, 0.03)' }}
      className='rounded-[16px] pt-2 border border-[#33A7E4] backdrop-blur-[27px]'
    >
      <div
        className={`flex w-fit gap-2 -mt-[2px] pb-1 mx-auto items-center ${isErrorCase ? 'text-[#FFE149]' : 'text-[#A2FFA0]'}`}
      >
        <div className='mb-[6px] pl-5'>•</div>
        <div className={`text-center font-[400] text-[14px] pb-1`}>{maturesText}</div>
        <div className='mb-[6px] pr-5'>•</div>
      </div>

      <div
        style={{ background: 'rgba(255, 255, 255, 0.03)' }}
        className={`flex items-center border-2 rounded-[16px] p-4 ${isFocused ? 'border-white' : 'border-[#33A7E4]'}`}
      >
        <span className='text-[28px] mr-1 text-white font-[500]'>₹</span>
        <div className='flex flex-1'>
          <input
            type='text'
            value={displayedValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder=''
            className='border-none outline-none text-[28px] font-[500] w-full bg-transparent text-white placeholder:text-white/3'
            ref={inputRef}
          />
        </div>
      </div>
    </div>
  )
}
